import { Injectable } from '@angular/core';
import { Action, State, StateContext } from '@ngxs/store';
import { finalize, switchMap, tap } from 'rxjs/operators';

import { LegalTermsService } from '@app/shared/services/legal-terms/legal-terms.service';
import { AppStateModel } from './app-state.model';
import {
  AcceptLegalTerms,
  AcceptLegalTermsAndPrivacyPolicy,
  AcceptPrivacyPolicy,
  FetchLegalTermsAndPrivacyPolicyUpdatedDate,
  FetchLegalTermsAndPrivacyPolicyUpdatedDateSuccess,
} from './app.actions';
import { combineLatest, Observable, of } from 'rxjs';
import { TokenService } from '@services/token.service';
import { TokenModel } from '@data-layer/models/user';

const APP_NAME = 'eezyimport';

const defaults: AppStateModel = {
  isLoading: false,
  updatedLegalTermsDate: null,
  updatedPrivacyPolicyDate: null,
};

@State<AppStateModel>({
  name: APP_NAME,
  defaults,
})
@Injectable()
export class AppState {
  constructor(private legalTermsService: LegalTermsService, private tokenService: TokenService) {}

  @Action(AcceptLegalTerms)
  public acceptLegalTerms(ctx: StateContext<AppStateModel>): Observable<any> {
    ctx.patchState({ isLoading: true });

    return this.legalTermsService.acceptLegalTerms().pipe(
      switchMap(() => this.tokenService.refreshToken(this.tokenService.getRefreshToken())),
      switchMap((token: TokenModel) => {
        this.tokenService.setToken(token);
        return of(true);
      }),
      finalize(() => ctx.patchState({ isLoading: false })),
    );
  }

  @Action(AcceptPrivacyPolicy)
  public acceptPrivacyPolicy(ctx: StateContext<AppStateModel>): Observable<any> {
    ctx.patchState({ isLoading: true });

    return this.legalTermsService.acceptPrivacyPolicy().pipe(
      switchMap(() => this.tokenService.refreshToken(this.tokenService.getRefreshToken())),
      switchMap((token: TokenModel) => {
        this.tokenService.setToken(token);
        return of(true);
      }),
      finalize(() => ctx.patchState({ isLoading: false })),
    );
  }

  @Action(AcceptLegalTermsAndPrivacyPolicy)
  public acceptLegalTermsAndPrivacyPolicy(ctx: StateContext<AppStateModel>): Observable<any> {
    ctx.patchState({ isLoading: true });

    return this.legalTermsService.acceptLegalTerms().pipe(
      switchMap(() => this.legalTermsService.acceptPrivacyPolicy()),
      switchMap(() => this.tokenService.refreshToken(this.tokenService.getRefreshToken())),
      switchMap((token: TokenModel) => {
        this.tokenService.setToken(token);
        return of(true);
      }),
      finalize(() => ctx.patchState({ isLoading: false })),
    );
  }

  @Action(FetchLegalTermsAndPrivacyPolicyUpdatedDate)
  public fetchLegalTermsAndPrivacyPolicyUpdatedDate(
    ctx: StateContext<AppStateModel>,
    { payload }: FetchLegalTermsAndPrivacyPolicyUpdatedDate,
  ): Observable<[string, string]> {
    ctx.patchState({ updatedLegalTermsDate: null, updatedPrivacyPolicyDate: null, isLoading: true });

    return this.legalTermsService.getLegalTermsLastUpdatedDate().pipe(
      switchMap((legalTermsDate: string) => combineLatest([of(legalTermsDate), this.legalTermsService.getPrivacyPolicyLastUpdatedDate()])),
      tap(([legalTermsDate, privacyPolicyDate]: [string, string]) => {
        ctx.patchState({ updatedLegalTermsDate: legalTermsDate, updatedPrivacyPolicyDate: privacyPolicyDate, isLoading: false });
        ctx.dispatch(new FetchLegalTermsAndPrivacyPolicyUpdatedDateSuccess({ legalTermsDate, privacyPolicyDate, redirect: payload }));
      }),
    );
  }
}
